import React, { type ReactNode } from 'react';

export interface CardProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  icon?: ReactNode;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({ children, className = '', style, title, icon, onClick }) => {
  return (
    <div
      style={style}
      className={`
        bg-white rounded-lg shadow-md
        p-4 sm:p-6
        ${onClick ? 'cursor-pointer hover:shadow-lg active:scale-[0.99]' : ''}
        transition-all duration-200
        ${className}
      `}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {(title || icon) && (
        <div className="flex items-center mb-3 sm:mb-4">
          {icon && <div className="mr-2 sm:mr-3">{icon}</div>}
          {title && (
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">
              {title}
            </h3>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: ReactNode;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'gray';
  onClick?: () => void;
  size?: 'compact' | 'default' | 'large';
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  color = 'blue',
  onClick,
  size = 'default',
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-50 text-gray-600',
  };

  const sizeClasses = {
    compact: {
      value: 'text-xl sm:text-2xl',
      icon: 'p-2',
      iconSize: 'w-8 h-8',
    },
    default: {
      value: 'text-2xl sm:text-3xl',
      icon: 'p-2 sm:p-3',
      iconSize: 'w-10 h-10',
    },
    large: {
      value: 'text-3xl sm:text-4xl',
      icon: 'p-3 sm:p-4',
      iconSize: 'w-12 h-12',
    },
  };

  const sizes = sizeClasses[size];

  return (
    <Card
      className={`${onClick ? 'hover:shadow-lg' : ''} transition-shadow`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1 truncate">
            {title}
          </p>
          <p className={`${sizes.value} font-bold text-gray-900`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
              {subtitle}
            </p>
          )}
          {trend && trendValue && (
            <div className="flex items-center mt-2">
              <span
                className={`text-xs font-medium flex items-center gap-1 ${
                  trend === 'up'
                    ? 'text-green-600'
                    : trend === 'down'
                    ? 'text-red-600'
                    : 'text-gray-600'
                }`}
              >
                {trend === 'up' ? (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                ) : trend === 'down' ? (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                  </svg>
                )}
                {trendValue}
              </span>
            </div>
          )}
        </div>
        {icon && (
          <div className={`${sizes.icon} rounded-full flex-shrink-0 ${colorClasses[color]}`}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * Grid container for cards with responsive columns
 */
interface CardGridProps {
  children: ReactNode;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}

export const CardGrid: React.FC<CardGridProps> = ({
  children,
  columns = 4,
  className = '',
}) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid ${colClasses[columns]} gap-3 sm:gap-4 lg:gap-6 ${className}`}>
      {children}
    </div>
  );
};
